import React, { useState, useEffect, useRef } from 'react'
import { string } from 'prop-types'

const minValues = {
  minPrice: 2,
  maxPrice: 10,
}

const maxValues = {
  minPrice: 450,
  maxPrice: 500,
}

function FastRange({ minPriceVal, maxPriceVal }) {
  const currentMax = useRef(maxPriceVal)
  const currentMin = useRef(minPriceVal)
  const [currentWidth, setCurrentWidth] = useState('100%')
  const [currentLeft, setCurrentLeft] = useState('0%')
  const [currentPreview, setCurrentPreview] = useState(minPriceVal)
  const [showTooltip, setShowTooltip] = useState(false)
  const [timeoutId, setTimeoutId] = useState(null)
  const [isDisabled, setDisabled] = useState(true)

  function getPercent(value) {
    const { minPrice: min } = minValues
    const { maxPrice: max } = maxValues

    return Math.round(((value - min) / (max - min)) * 100)
  }

  function adjustPercentage(min, max) {
    setCurrentWidth(`${max - min}%`)
    setCurrentLeft(`${min}%`)
  }

  function handleChange(target, value) {
    const isForMin = target === 'minPrice'
    const normalizedValue = isForMin ? Math.min(value, Number(currentMax.current) - 1)
      : Math.max(value, Number(currentMin.current) + 1)

    if (normalizedValue < minValues.minPrice || normalizedValue > maxValues.maxPrice) {
      return null
    }

    const minPercent = getPercent(isForMin ? normalizedValue : currentMin.current)
    const maxPercent = getPercent(isForMin ? currentMax.current : normalizedValue)


    setShowTooltip(true)

    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    adjustPercentage(minPercent, maxPercent)
    setCurrentPreview(normalizedValue)
    setTimeoutId(setTimeout(() => setShowTooltip(false), 1000))

    return isForMin ? currentMin.current = normalizedValue : currentMax.current = normalizedValue
  }

  function handleInputEnd() {
    window.handleFastRangeChange(currentMin.current, currentMax.current)
  }

  function renderInput(name) {
    const value = name === 'minPrice' ? currentMin.current : currentMax.current

    return (
      <input
        type="range"
        name={name}
        className="dual-range__input"
        value={value}
        onChange={(e) => { handleChange(name, Number(e.target.value)) }}
        onMouseUp={() => { handleInputEnd() }}
        min={minValues[name]}
        max={maxValues[name]}
        disabled={isDisabled}
      />
    )
  }

  useEffect(() => {
    const minPercent = getPercent(minPriceVal)
    const maxPercent = getPercent(maxPriceVal)

    adjustPercentage(minPercent, maxPercent)
    setDisabled(false)

    return () => !!timeoutId && clearTimeout(timeoutId)
  }, [])

  return (
    <div className="dual-range">
      {renderInput('minPrice')}
      {renderInput('maxPrice')}
      <div className="dual-range__track-bg" />
      <div style={{ left: currentLeft, width: currentWidth }} className="dual-range__track" />
      <div className="min-value">${minValues.minPrice}</div>
      <div className="max-value">${maxValues.maxPrice}+</div>
      {showTooltip && (
        <span className="tooltip active">
          <span className="arrow" />
          <span className="content">
            ${currentPreview}
          </span>
        </span>
      )}
    </div>
  )
}

FastRange.propTypes = {
  minPriceVal: string.isRequired,
  maxPriceVal: string.isRequired,
}

export default FastRange
